import classNames from 'classnames'
import { FormikProps, withFormik } from 'formik'
import React, { FC, SyntheticEvent, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { Form } from '@lib/formik'

import { useModal } from '@context/ModalsContext'
import { usePathHistoryContext } from '@context/PathHistoryContext'

import { Body1 } from '@ui/Typography'
import { Button } from '@ui/components/Button'

import { ValidationOperationId } from '@model/ValidationDto'

import { usePasswordValidation } from '@global/PasswordValidation/PasswordValidation'
import { createFormPasswordField } from '@global/formField/FormPasswordField'
import { FormTemplate } from '@global/templates/FormTemplate'
import { TransactionFormActionButtonTemplate } from '@global/templates/TransactionFormActionButtonTemplate'

import { scrollToTop } from '@hooks/useScrollToTop'

import { Box } from '@shared/components/Box'
import { ActionModal } from '@shared/modals/Action.modal'

import styles from './ChangeTradingAccountPasswordForm.module.scss'

export interface ChangeTradingAccountPasswordValues {
  tradingAccountId: string
  password: string
  changeInvestorPassword: boolean
}

interface OuterProps {
  title?: string
  onSubmit: (values: ChangeTradingAccountPasswordValues) => void
  tradingAccountId: string
  changeInvestorPassword: boolean
  validationError?: string
  changeTickmillTraderPassword: boolean
  isButtonDisabled?: boolean
}

const PasswordFormField = createFormPasswordField<ChangeTradingAccountPasswordValues>()

type ChangeTradingAccountPasswordFormType = React.FC<
  FormikProps<ChangeTradingAccountPasswordValues> & OuterProps
>

const ChangeTradingAccountPasswordFormUI: ChangeTradingAccountPasswordFormType = (props) => {
  scrollToTop()

  const {
    handleSubmit,
    setSubmitting,
    title,
    isValid,
    isSubmitting,
    dirty,
    values,
    isButtonDisabled,
  } = props
  const { changeInvestorPassword, changeTickmillTraderPassword, validationError } = props
  const { password } = values

  const { t } = useTranslation()

  const { navigateToPreviousPath } = usePathHistoryContext()

  const { isPasswordValid, validationComponent } = usePasswordValidation({
    password,
    validationOperationId: ValidationOperationId.ValidateMetatraderAccountPassword,
  })

  const cancelModal = useModal(ActionModal)

  const newPasswordLabel = useMemo(() => {
    if (changeInvestorPassword) {
      return t('Trading Account.New Investor Password')
    } else if (changeTickmillTraderPassword) {
      return t('Trading Account.New User ID Password')
    }
    return t('Trading Account.New Trading Account Password')
  }, [t, changeInvestorPassword, changeTickmillTraderPassword])

  const passwordLabel = useMemo(() => {
    if (changeInvestorPassword) {
      return t('Trading Account.Change Investor Password')
    } else if (changeTickmillTraderPassword) {
      return t('Trading Account.Change User ID Password')
    }
    return t('Trading Account.Change Trading Account Password')
  }, [t, changeInvestorPassword, changeTickmillTraderPassword])

  const handleSubmitForm = (event: SyntheticEvent) => {
    event.preventDefault()
    setSubmitting(true)
    handleSubmit()
  }

  return (
    <FormTemplate title={title || passwordLabel} wrapped headerDisplayType='flex'>
      <Form className={styles.wrapper} onSubmit={handleSubmitForm}>
        {!changeInvestorPassword && (
          <ImportantInfo changeTickmillTraderPassword={changeTickmillTraderPassword} />
        )}

        <Box flex direction='column' gap='x16' mb='x32'>
          <PasswordFormField
            name='password'
            label={newPasswordLabel}
            type='password'
            required
            error={validationError}
          />
          {validationComponent}
        </Box>

        <TransactionFormActionButtonTemplate>
          <Button.Secondary
            size='lg'
            type='button'
            fullWidth
            onClick={() => cancelModal.open({ onConfirm: navigateToPreviousPath })}
          >
            {t('Cancel')}
          </Button.Secondary>
          <Button.Primary
            size='lg'
            type='submit'
            fullWidth
            disabled={!(isPasswordValid && isValid && !isSubmitting && dirty) || isButtonDisabled}
          >
            {t('Confirm')}
          </Button.Primary>
        </TransactionFormActionButtonTemplate>
      </Form>
    </FormTemplate>
  )
}

export const ChangeTradingAccountPasswordForm = withFormik<
  OuterProps,
  ChangeTradingAccountPasswordValues
>({
  mapPropsToValues: ({ tradingAccountId, changeInvestorPassword }) => {
    return {
      password: '',
      tradingAccountId,
      changeInvestorPassword,
    }
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      props.onSubmit(values)
    } finally {
      setSubmitting(false)
    }
  },
})(ChangeTradingAccountPasswordFormUI)

export const ImportantInfo: FC<{ changeTickmillTraderPassword: boolean }> = ({
  changeTickmillTraderPassword,
}) => {
  const { t } = useTranslation()

  const message = changeTickmillTraderPassword
    ? t('Trading Account.Trading User ID Password is not your Tickmill account password')
    : t('Trading Account.Trading Account Password is not your Tickmill account password.')

  return (
    <Body1 className={classNames(styles.info)}>
      <span>{t('Trading Account.Important')}</span> {message}
    </Body1>
  )
}
